import Button from "components/form/Button";
import { PaymentGateway, SettingsValue } from "types/payments";
import { FilledInfoIcon } from "icons/payments/FilledInfoIcon";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import Skeleton from "react-loading-skeleton";
import { PlaidLinkError, usePlaidLink } from "react-plaid-link";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  updateCheckAmountState,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  duplicatePaymentGet,
  getPlaidPaymentToken,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
} from "store/slices/paymentsSlice";
import {
  checkOverPaymentAmount,
  checkPhone,
  decode,
  formatCurrency,
  validateEmail,
} from "utils/HelperFunctions";
import { RootState } from "store/store";
import { PhoneInput } from "types/common";
import { useTid } from "utils/testid";
import { getPaymentTotalWithFee } from "utils/paymentFee";

interface PlaidBankFormProps {
  amount: any;
  linkToken: string | null;
  isLoading: boolean;
  handlePlaidPayment: (
    value: any,
    isCCTokenAvailable: boolean
  ) => Promise<void>;
  isAgreement?: boolean;
  handleBack?: any;
  isUpdatePlan?: boolean;
  isSchedulePayment?: boolean;
  bankRef?: any;
  isEdit?: boolean;
  email: string;
  isRetry?: boolean;
  isEmailInputBlur: boolean;
  isProductLink: boolean;
  isUpdateSubscription: boolean;
  checkNegativeBalance?: boolean;
  accounts_id?: string | null;
  isUpdateSubscriptionPaymentMethod?: boolean;
  isDowngradePlan?: boolean;
  collectPhone?: boolean;
  phoneNumber?: PhoneInput;
  setPhoneNumber?: React.Dispatch<React.SetStateAction<PhoneInput>>;
}

interface LinkFormProps {
  amount: any;
  linkToken: string | null;
  isLoading: boolean;
  handlePlaidPayment: (
    value: any,
    isCCTokenAvailable: boolean
  ) => Promise<void>;
  isUpdatePlan?: boolean;
  conFee: number;
  isRetry?: boolean;
  isAgreement?: boolean;
  handleBack?: any;
  isSchedulePayment?: boolean;
  isEdit?: boolean;
  email: string;
  isEmailInputBlur: boolean;
  isProductLink: boolean;
  isUpdateSubscription: boolean;
  checkNegativeBalance?: boolean;
  accounts_id?: string | null;
  collectPhone?: boolean;
  phoneNumber?: PhoneInput;
  isUpdateSubscriptionPaymentMethod?: boolean;
  isDowngradePlan?: boolean;
  setPhoneNumber?: React.Dispatch<React.SetStateAction<PhoneInput>>;
}

const PlaidBankForm = (props: PlaidBankFormProps) => {
  const tid = useTid();
  const {
    linkToken,
    isLoading,
    handlePlaidPayment,
    isAgreement = false,
    handleBack = () => {},
    isUpdatePlan = false,
    amount,
    isSchedulePayment = false,
    bankRef,
    isRetry = false,
    isEdit,
    email,
    isEmailInputBlur,
    isProductLink = false,
    isUpdateSubscription = false,
    checkNegativeBalance = false,
    isUpdateSubscriptionPaymentMethod = false,
    accounts_id = "",
    collectPhone = false,
    phoneNumber = { phone: "", country_code: "", postal_code: "" },
    setPhoneNumber = () => {},
    isDowngradePlan = false,
  } = props;

  const { conFee, isFeeLoading } = useAppSelector(
    (state: RootState) => state.payment
  );
  const { isChecking } = useAppSelector(
    (state: RootState) => state.subscription
  );

  return (
    <div data-testid={tid("plaid-bank-form")} aria-label="Plaid bank form">
      {linkToken ? (
        <LinkForm
          linkToken={linkToken}
          isLoading={isLoading || isFeeLoading || isChecking}
          handlePlaidPayment={handlePlaidPayment}
          amount={amount}
          isUpdatePlan={isUpdatePlan}
          conFee={conFee}
          isRetry={isRetry}
          isAgreement={isAgreement}
          handleBack={handleBack}
          isSchedulePayment={isSchedulePayment}
          ref={bankRef}
          isEdit={isEdit}
          email={email}
          isEmailInputBlur={isEmailInputBlur}
          isProductLink={isProductLink}
          isUpdateSubscription={isUpdateSubscription}
          checkNegativeBalance={checkNegativeBalance}
          accounts_id={accounts_id}
          collectPhone={collectPhone}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          isUpdateSubscriptionPaymentMethod={isUpdateSubscriptionPaymentMethod}
          isDowngradePlan={isDowngradePlan}
        />
      ) : (
        !isRetry &&
        (isAgreement ? (
          <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
            <Button
              text={`Back`}
              variant="white"
              onClick={handleBack}
              className="lg:w-40 w-full max-w-40"
              data-testid={tid("plaid-bank-disabled-back-button")}
              aria-label="Go back from Plaid bank payment"
            />
            <Button
              text={`Confirm`}
              isDisabled={true}
              isLoading={isLoading || isFeeLoading}
              className="lg:w-40 w-full max-w-40"
              variant="variable"
              data-testid={tid("plaid-bank-disabled-confirm-button")}
              aria-label="Confirm Plaid bank payment"
            />
          </div>
        ) : isSchedulePayment ? (
          <div className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5">
            <div
              className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
            >
              {isUpdatePlan && (
                <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
              )}

              <div className="w-full flex justify-between gap-5">
                <Button
                  className="lg:!w-40 !w-full"
                  isDisabled={isLoading || isFeeLoading}
                  text="Back"
                  type="button"
                  variant="secondary"
                  onClick={handleBack}
                  data-testid={tid("plaid-bank-disabled-schedule-back-button")}
                  aria-label="Go back from scheduled Plaid bank payment"
                />
                <Button
                  className="lg:!w-40 !w-full"
                  isLoading={isLoading || isFeeLoading}
                  isDisabled={true}
                  text={isUpdatePlan ? "Update" : "Confirm"}
                  type="button"
                  variant="variable"
                  data-testid={tid(
                    "plaid-bank-disabled-schedule-confirm-button"
                  )}
                  aria-label={
                    isUpdatePlan
                      ? "Update scheduled Plaid bank payment"
                      : "Confirm scheduled Plaid bank payment"
                  }
                />
              </div>
            </div>
          </div>
        ) : (
          <Button
            text={
              isUpdateSubscription || isUpdateSubscriptionPaymentMethod
                ? "Update"
                : `Pay ${formatCurrency(getPaymentTotalWithFee(amount, conFee)) || 0.0}`
            }
            className={"!w-full mt-5"}
            isDisabled={true}
            isLoading={isLoading || isFeeLoading || isChecking}
            variant={"variable"}
            data-testid={tid("plaid-bank-disabled-submit-button")}
            aria-label={
              isUpdateSubscription || isUpdateSubscriptionPaymentMethod
                ? "Update Plaid bank payment"
                : "Pay with Plaid bank"
            }
          />
        ))
      )}
    </div>
  );
};

type LinkFormData = {
  publicToken: string;
  accountId: string;
  bankName: string;
};

const LinkForm = forwardRef(
  (
    {
      linkToken,
      isLoading,
      handlePlaidPayment,
      isUpdatePlan = false,
      amount = 0,
      conFee = 0,
      isRetry = false,
      isAgreement = false,
      handleBack = () => {},
      isSchedulePayment = false,
      isEdit = false,
      email,
      isEmailInputBlur,
      isProductLink,
      isUpdateSubscription = false,
      checkNegativeBalance,
      accounts_id = "",
      isUpdateSubscriptionPaymentMethod = false,
      isDowngradePlan = false,
      collectPhone = false,
      phoneNumber = { phone: "", country_code: "", postal_code: "" },
      setPhoneNumber = () => {},
    }: LinkFormProps,
    ref
  ) => {
    const tid = useTid();
    const [isMakingPayment, setIsMakingPayment] = useState<boolean>(false);
    const userData = useUserData();
    const dispatch = useAppDispatch();
    const { clusterId: cluster_id, id } = useParams();
    const clusterId = decode(cluster_id || "", true);
    const portalSettings = usePortalSettingsData();
    const showFinalDisclosure = useShowFinalDisclosure();
    const { checkAmount } = useAppSelector((state: RootState) => state.common);
    const { checkedDuplicatePayment } = useAppSelector(
      (state: RootState) => state.payment
    );
    const { isSubscriptionAllowed } = useAppSelector(
      (state: RootState) => state.subscription
    );

    const getPaymentToken = async (data: LinkFormData) => {
      setIsMakingPayment(true);
      try {
        let clusterid = clusterId
          ? clusterId
          : userData?.data?.accountData?.clusterid
            ? userData?.data?.accountData?.clusterid
            : id
              ? decode(id || "")?.split("_")[1]
              : "";

        let payload = {
          public_token: data?.publicToken,
          bankAccountID: data?.accountId,
          clusterid: clusterid,
          accountid: userData?.data?.accountData?.accountid,
          email: email,
          isProductLink,
          slug: cluster_id || id || "",
        };
        let res: any = await dispatch(getPlaidPaymentToken(payload)).unwrap();
        if (res?.body?.success) {
          const { address } = res?.body?.data;
          let values = {
            bankName: data?.bankName,
            bank: data?.bankName,
            choldername: userData?.data?.accountData?.first_name,
            routing_number: res?.body?.data?.routing_numebr,
            account_number: res?.body?.data?.account_number,
            gateway_name: res?.body?.data?.gateway_name,
            x_city: address?.city ?? "",
            x_address: address?.street ?? "",
            address2: "",
            x_zip: address?.postal_code ?? "",
            x_state: address?.region ?? "",
            x_country: address?.country ?? "",
            email,
            gateway_id: res?.body?.data?.gatewayId,
            cc_token: res?.body?.data?.cc_token,
            cc_token2: res?.body?.data?.cc_token2,
            pc_atid: res?.body?.data?.pc_atid,
            cppid: res?.body?.data?.cppid,
            x_payment_method: "ACH",
          };
          let isCCTokenAvailable =
            res?.body?.data?.cc_token &&
            res?.body?.data?.cc_token?.trim() !== "" &&
            res?.body?.data?.gateway_name === PaymentGateway.ACHQ;
          await handlePlaidPayment(values, isCCTokenAvailable);
        } else {
          dispatch(onSetFinalDisclosureLoading(false));
          dispatch(onSetShowDisclosure(false));
        }
      } catch (error) {
        console.log("getPaymentTokenerror =====", error);
      } finally {
        setIsMakingPayment(false);
        dispatch(onSetFinalDisclosureLoading(false));
        dispatch(onSetShowDisclosure(false));
      }
    };

    const onSuccess = useCallback(
      async (public_token?: string, metadata?: any) => {
        if (public_token && metadata) {
          let data = {
            publicToken: public_token,
            accountId: metadata?.account_id,
            bankName: metadata?.institution?.name,
          };
          await getPaymentToken(data);
        }
      },
      []
    );

    const config: Parameters<typeof usePlaidLink>[0] = {
      token: linkToken!,
      onSuccess: onSuccess,
      onExit: (error: PlaidLinkError | null) => {
        dispatch(onSetFinalDisclosureLoading(false));
        dispatch(onSetShowDisclosure(false));
        setIsMakingPayment(false);
        console.log("error =====", error);
      },
    };

    const { open, ready } = usePlaidLink(config);

    const checkPastAmount = async () => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id ?? accounts_id,
          payment_method: "ACH",
          amount: amount,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async () => {
      try {
        let moveAhead =
          !checkedDuplicatePayment && !isProductLink
            ? await checkPastAmount()
            : true;
        if (!moveAhead) {
          dispatch(onSetFinalDisclosureLoading(false));
          return false;
        } else {
          if (
            (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
              portalSettings?.payment_agreement_opt ===
                SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(resetDuplicateOrder());
            dispatch(onSetShowDisclosure(true));
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handlePlaidBank = async () => {
      setIsMakingPayment(true);
      try {
        if (!isUpdateSubscriptionPaymentMethod) {
          let negativeBalance =
            !checkAmount && checkNegativeBalance
              ? await checkOverPaymentAmount({
                  amount: Number(amount),
                  balance: userData?.data?.accountData?.balance,
                  overpayment_protection:
                    portalSettings?.overpayment_protection,
                  dispatch,
                })
              : true;
          if (!negativeBalance) {
            setIsMakingPayment(false);
            return;
          }

          let moveAhead = await handleCheckDisclosureAndDuplicate();
          if (!moveAhead) {
            setIsMakingPayment(false);
            return;
          }
        }
        dispatch(updateCheckAmountState(false));
        open();
      } catch (error) {
        console.log("error =====", error);
      }
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () => handlePlaidBank(),
    }));

    return (
      <>
        {!isRetry &&
          (isAgreement ? (
            <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
              <Button
                text={`Back`}
                variant="white"
                onClick={handleBack}
                className="lg:w-40 w-full max-w-40"
                isDisabled={isLoading || isMakingPayment}
                data-testid={tid("plaid-bank-back-button")}
                aria-label="Go back from Plaid bank payment"
              />
              <Button
                text={`Confirm`}
                isDisabled={!ready || isEdit}
                isLoading={isLoading || isMakingPayment}
                onClick={() => {
                  handlePlaidBank();
                }}
                className="lg:w-40 w-full max-w-40"
                variant="variable"
                data-testid={tid("plaid-bank-confirm-button")}
                aria-label="Confirm Plaid bank payment"
              />
            </div>
          ) : isSchedulePayment ? (
            <div className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5">
              <div
                className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
              >
                {isUpdatePlan && (
                  <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
                )}

                <div className="w-full flex justify-between gap-5">
                  <Button
                    className="lg:!w-40 !w-full"
                    isDisabled={isLoading || isMakingPayment}
                    text="Back"
                    type="button"
                    variant="secondary"
                    onClick={handleBack}
                    data-testid={tid("plaid-bank-schedule-back-button")}
                    aria-label="Go back from scheduled Plaid bank payment"
                  />
                  <Button
                    className="lg:!w-40 !w-full"
                    isLoading={isLoading || isMakingPayment}
                    isDisabled={!ready || isEdit}
                    onClick={() => {
                      handlePlaidBank();
                    }}
                    text={isUpdatePlan ? "Update" : "Confirm"}
                    type="button"
                    variant="variable"
                    data-testid={tid("plaid-bank-schedule-confirm-button")}
                    aria-label={
                      isUpdatePlan
                        ? "Update scheduled Plaid bank payment"
                        : "Confirm scheduled Plaid bank payment"
                    }
                  />
                </div>
              </div>
            </div>
          ) : (
            <Button
              text={
                isUpdatePlan ||
                isUpdateSubscription ||
                isUpdateSubscriptionPaymentMethod
                  ? "Update"
                  : `Pay ${formatCurrency(getPaymentTotalWithFee(amount, conFee)) || 0.0}`
              }
              className={"!w-full mt-5"}
              isDisabled={
                !ready ||
                isEdit ||
                !isEmailInputBlur ||
                (isProductLink &&
                  (!validateEmail(email) || !isSubscriptionAllowed)) ||
                (collectPhone && !checkPhone(phoneNumber)) ||
                (isUpdateSubscriptionPaymentMethod || isDowngradePlan
                  ? false
                  : amount <= 1)
              }
              onClick={() => {
                handlePlaidBank();
              }}
              isLoading={isLoading || isMakingPayment}
              variant={"variable"}
              data-testid={tid("plaid-bank-submit-button")}
              aria-label={
                isUpdatePlan ||
                isUpdateSubscription ||
                isUpdateSubscriptionPaymentMethod
                  ? "Update Plaid bank payment"
                  : "Pay with Plaid bank"
              }
            />
          ))}
      </>
    );
  }
);

export default PlaidBankForm;
